import { useState, useMemo, useRef, useEffect } from "react";
import {
  Users, MessageCircle, Sparkles, Mail, Clock, Globe, BarChart3,
  Bell, TrendingUp, UserCog, DollarSign, Eye, FlaskConical, Lock,
  ChevronDown, Search, Layers, Megaphone, Wrench, Settings2, X,
  Zap, Rocket, Database, ExternalLink, Code2,
  BookOpen, MessageSquare, Star, Filter,
} from "lucide-react";
import { getAdminTabMeta, getPopularTabs } from "./adminTabDescriptions";

/**
 * Grouped, searchable admin navigation.
 * - 4 top-level group pills (Overview, Marketing, Product, Ops)
 * - Click a group → a popover opens with the items in that group
 * - Active item's group pill is highlighted and shows its label inline
 * - A search input at the top filters across ALL groups in real time
 * - Keeps every `admin-tab-${key}` test-id identical to the old flat grid
 *   so existing e2e tests keep passing.
 */

const ICONS = {
  leads: Users, chats: MessageCircle, discounts: Sparkles, emails: Mail,
  rescans: Clock, seo: Globe, analytics: BarChart3, social: Users,
  ads: Sparkles, notifs: Bell, brand: TrendingUp, pulseai: Sparkles,
  theme: Sparkles, users: UserCog, pricing: DollarSign, behavior: Eye,
  fbig_ads: Sparkles, fb_ads_library: Megaphone, referral: TrendingUp, users_mgmt: Users,
  ab_stats: FlaskConical, security: Lock,
  drip_ab: FlaskConical,
  cinematic_ads: FlaskConical,
  visionary_copy: BookOpen,
  seo_indexing: BookOpen,
  funnel: Zap,
  waitlist: Bell,
  agent_demand: Zap,
  affiliates: DollarSign,
  eshop_tiers: Layers,
  faqs: BookOpen,
  chat_feedback: MessageSquare,
  invoicing: DollarSign,
  ad_studio: Megaphone,
  ad_creative_studio: Sparkles,
  launch_checklist: Zap,
  anti_rankpill: Lock,
  leads_export: Layers,
  gsc_setup: Globe,
  sitemap_ping: Rocket,
  seed_production: Database,
  studio_autopilot: Sparkles,
  mika_ads: Megaphone,
  diagnostic_funnel: Zap,
  manifest_ab: Zap,
  hero_ab: FlaskConical,
  index_now: Search,
  fb_ad_campaigns: Megaphone,
  challenge_5day: Zap,
  api_widget_stats: BarChart3,
  api_email_stats: Mail,
  api_partner_share: ExternalLink,
  api_v1_docs: Code2,
  url_analytics: BarChart3,
  persona_ads: Megaphone,
  funnel_campaigns: Megaphone,
  funnel_summary: BarChart3,
};

function tabsFor(t) {
  return {
    overview: {
      label: t.admin_group_overview || "Prehľad",
      Icon: Layers,
      items: [
        { key: "leads", label: t.admin_leads || "Leady" },
        { key: "chats", label: t.admin_chats || "Chat logy" },
        { key: "analytics", label: "Analytika" },
        { key: "notifs", label: "Notifikácie" },
        { key: "waitlist", label: "Waitlist" },
        { key: "affiliates", label: "Affiliate" },
        { key: "security", label: "Bezpečnosť" },
      ],
    },
    funnely: {
      label: "Funnely",
      Icon: Filter,
      items: [
        { key: "funnel_summary", label: "💶 Súhrn funnelov (tržby a reklama)" },
        { key: "funnel_campaigns", label: "🧲 Funnely a kampane" },
        { key: "persona_ads", label: "🎯 Persona funnely (agentúry / firmy)" },
        { key: "url_analytics", label: "📈 Výsledky funnelov (URL Analytika)" },
        { key: "funnels", label: "🔗 Všetky funnely URL" },
        { key: "diagnostic_funnel", label: "📊 Diagnostika Funnel" },
        { key: "funnel", label: "🎥 VSL Funnely" },
        { key: "challenge_5day", label: "🏁 5-Day Challenge" },
        { key: "hero_ab", label: "🧪 Hero A/B (4 varianty)" },
        { key: "manifest_ab", label: "🧪 Manifest A/B" },
      ],
    },
    marketing: {
      label: t.admin_group_marketing || "Marketing",
      Icon: Megaphone,
      items: [
        { key: "launch_checklist", label: "🚀 Launch Checklist" },
        { key: "fb_ad_campaigns", label: "📣 FB Reklamy" },
        { key: "ad_creative_studio", label: "🎨 Ad Creative Studio" },
        { key: "studio_autopilot", label: "🤖 Studio Auto-Pilot" },
        { key: "mika_ads", label: "🎭 Mika Ad Gallery" },
        { key: "ad_studio", label: "🎬 Ad Studio (video)" },
        { key: "discounts", label: t.admin_discounts || "Zľavové kódy" },
        { key: "emails", label: "Emaily" },
        { key: "ab_stats", label: "A/B Emaily" },
        { key: "drip_ab", label: "Drip A/B" },
        { key: "cinematic_ads", label: "Cinematic Ads" },
        { key: "visionary_copy", label: "Visionary Copy" },
        { key: "jendral_story", label: "📝 Jendrál Story Gen" },
        { key: "case_studies", label: "📊 Scan Case Studies" },
        { key: "seo_indexing", label: "SEO Indexing" },
        { key: "ads", label: "Reklamy" },
        { key: "fbig_ads", label: "FB/IG Stratégia" },
        { key: "fb_ads_library", label: "FB Ads Library" },
        { key: "referral", label: "Referral" },
        { key: "social", label: "Social Proof" },
      ],
    },
    product: {
      label: t.admin_group_product || "Produkt",
      Icon: Wrench,
      items: [
        { key: "pulseai", label: "PulseAI™" },
        { key: "agents", label: "🤖 AI Agenti" },
        { key: "roadmap", label: "🛠️ Roadmap" },
        { key: "gsc_setup", label: "🔍 GSC Setup" },
        { key: "index_now", label: "🔎 IndexNow (Bing/Yandex)" },
        { key: "sitemap_ping", label: "📡 Sitemap Ping" },
        { key: "anti_rankpill", label: "🛡️ Anti-RankPill" },
        { key: "theme", label: "Dizajn" },
        { key: "brand", label: "Brand" },
        { key: "seo", label: "SEO / GEO" },
        { key: "rescans", label: "Opak. skeny" },
        { key: "faqs", label: "Chat FAQ" },
        { key: "chat_feedback", label: "Chat Feedback" },
        { key: "agent_demand", label: "Agent Demand" },
      ],
    },
    ops: {
      label: t.admin_group_ops || "Prevádzka",
      Icon: Settings2,
      items: [
        { key: "users", label: "Používatelia" },
        { key: "users_mgmt", label: "Správa účtov" },
        { key: "pricing", label: "Kurzy & Ceny" },
        { key: "eshop_tiers", label: "E-shop Tiers" },
        { key: "behavior", label: "Správanie" },
        { key: "invoicing", label: "💼 Fakturácia" },
        { key: "leads_export", label: "📊 Leads CSV Export" },
        { key: "seed_production", label: "🌱 Seed Production" },
      ],
    },
    api: {
      label: t.admin_group_api || "API & Embedy",
      Icon: Code2,
      items: [
        {
          key: "api_widget_stats",
          label: "📊 Widget Stats Dashboard",
          href: "/api/static/embed/widget-stats.html",
        },
        {
          key: "api_email_stats",
          label: "📧 Email Stats + Anomálie",
          href: "/api/static/embed/email-stats.html",
        },
        {
          key: "api_partner_share",
          label: "🔗 Partner share-link (stavebne-dopyty.sk)",
          href: "/api/static/embed/partner-stats.html?token=trp_share_omAKzWKCdxsSAOZYM7K9iYFxog",
        },
        {
          key: "api_v1_docs",
          label: "📜 Public API v1 (Postman snippet)",
          href: "/api/static/embed/api-v1-snippets.html",
        },
      ],
    },
  };
}

export default function AdminGroupedTabs({ t, activeTab, onSelect, counts = {} }) {
  const groups = useMemo(() => tabsFor(t), [t]);
  const [openGroup, setOpenGroup] = useState(null);
  const [query, setQuery] = useState("");
  const popRef = useRef(null);

  // Figure out which group contains the currently active tab for highlighting.
  const activeGroup = useMemo(() => {
    for (const [gk, g] of Object.entries(groups)) {
      if (g.items.some((it) => it.key === activeTab)) return gk;
    }
    return null;
  }, [activeTab, groups]);

  // Close popover on outside click / escape.
  useEffect(() => {
    if (!openGroup && !query) return;
    function onDown(e) {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setOpenGroup(null);
        setQuery("");
      }
    }
    function onKey(e) {
      if (e.key === "Escape") { setOpenGroup(null); setQuery(""); }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openGroup, query]);

  const searchHits = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.trim().toLowerCase();
    const hits = [];
    Object.values(groups).forEach((g) => {
      g.items.forEach((it) => {
        if (it.label.toLowerCase().includes(q) || it.key.includes(q)) {
          hits.push({ ...it, groupLabel: g.label });
        }
      });
    });
    return hits;
  }, [query, groups]);

  const activeItemLabel = useMemo(() => {
    for (const g of Object.values(groups)) {
      const f = g.items.find((it) => it.key === activeTab);
      if (f) return f.label;
    }
    return activeTab;
  }, [activeTab, groups]);

  return (
    <div ref={popRef} className="relative mb-6" data-testid="admin-grouped-nav">
      {/* Active-item breadcrumb bar */}
      <div className="flex items-center gap-2 mb-3 text-[13px] text-gray-500">
        <span>{groups[activeGroup]?.label}</span>
        <ChevronDown className="w-3 h-3 -rotate-90" />
        <span className="font-bold text-gray-900">{activeItemLabel}</span>
      </div>

      {/* 💡 Popular quick-access strip */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="admin-nav-popular">
        <span className="text-[10px] uppercase tracking-widest text-amber-600 font-black flex items-center gap-1">
          <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Najpoužívanejšie
        </span>
        {getPopularTabs().map((p) => {
          const Icon = ICONS[p.key] || Sparkles;
          const isActive = activeTab === p.key;
          return (
            <button
              key={`pop-${p.key}`}
              type="button"
              onClick={() => onSelect(p.key)}
              data-testid={`admin-popular-${p.key}`}
              title={p.description}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold transition-all border ${
                isActive
                  ? "bg-amber-500 text-black border-amber-500"
                  : "bg-white text-gray-700 border-amber-200 hover:bg-amber-50 hover:border-amber-400"
              }`}
            >
              <Icon className="w-3 h-3" />
              <span>
                {(groups.overview.items.find((i) => i.key === p.key) ||
                  groups.funnely.items.find((i) => i.key === p.key) ||
                  groups.marketing.items.find((i) => i.key === p.key) ||
                  groups.product.items.find((i) => i.key === p.key) ||
                  groups.ops.items.find((i) => i.key === p.key) || {}).label || p.key}
              </span>
              {p.badge && (
                <span className="text-[8px] uppercase tracking-widest bg-amber-300 text-amber-900 px-1 rounded font-black">
                  {p.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Group pills + inline search */}
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(groups).map(([gk, g]) => {
          const Icon = g.Icon;
          const isActive = gk === activeGroup;
          const isOpen = gk === openGroup;
          return (
            <button
              key={gk}
              type="button"
              onClick={() => setOpenGroup(isOpen ? null : gk)}
              data-testid={`admin-group-${gk}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-bold transition-all border ${
                isActive
                  ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                  : isOpen
                    ? "bg-gray-100 text-gray-900 border-gray-300"
                    : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{g.label}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
          );
        })}

        {/* Search */}
        <div className="flex items-center gap-2 ml-auto relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.admin_nav_search || "Hľadať sekciu…"}
            data-testid="admin-nav-search"
            className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg w-56 focus:outline-none focus:border-gray-900"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 text-gray-400 hover:text-gray-700"
              aria-label="clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Search results popover (overrides group popover when query is active) */}
      {query && searchHits && (
        <div
          data-testid="admin-nav-search-results"
          className="absolute left-0 right-0 top-full mt-2 z-30 bg-white border border-gray-200 rounded-xl shadow-xl p-2 max-h-[520px] overflow-auto"
        >
          {searchHits.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">
              {t.admin_nav_no_results || "Žiadne výsledky."}
            </div>
          ) : (
            searchHits.map((it) => (
              <NavItemButton
                key={it.key}
                item={it}
                counts={counts}
                active={it.key === activeTab}
                subLabel={it.groupLabel}
                onClick={() => {
                  if (it.href) {
                    window.open(it.href, "_blank", "noopener,noreferrer");
                  } else {
                    onSelect(it.key);
                  }
                  setQuery("");
                }}
              />
            ))
          )}
        </div>
      )}

      {/* Group popover */}
      {!query && openGroup && (
        <div
          data-testid={`admin-group-popover-${openGroup}`}
          className="absolute left-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-xl shadow-xl p-2 min-w-[380px] max-w-[440px] max-h-[520px] overflow-auto"
        >
          {groups[openGroup].items.map((it) => (
            <NavItemButton
              key={it.key}
              item={it}
              counts={counts}
              active={it.key === activeTab}
              onClick={() => {
                if (it.href) {
                  window.open(it.href, "_blank", "noopener,noreferrer");
                } else {
                  onSelect(it.key);
                }
                setOpenGroup(null);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NavItemButton({ item, counts, active, onClick, subLabel }) {
  const Icon = ICONS[item.key] || Sparkles;
  const count = counts[item.key] || 0;
  const meta = getAdminTabMeta(item.key);
  const badge = meta?.badge;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`admin-tab-${item.key}`}
      className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors text-left ${
        active
          ? "bg-gray-900 text-white"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate">{item.label}</span>
          {item.href && (
            <ExternalLink className={`w-3 h-3 flex-shrink-0 ${active ? "text-white/80" : "text-amber-600"}`} />
          )}
          {badge && (
            <span
              className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-black ${
                badge === "NEW"
                  ? "bg-amber-100 text-amber-700"
                  : badge === "HOT"
                    ? "bg-rose-100 text-rose-700"
                    : "bg-blue-100 text-blue-700"
              }`}
            >
              {badge}
            </span>
          )}
          {subLabel && (
            <span className={`ml-auto text-[10px] uppercase tracking-wider ${active ? "text-white/60" : "text-gray-400"}`}>
              {subLabel}
            </span>
          )}
          {count > 0 && (
            <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? "bg-white/20" : "bg-gray-100 text-gray-600"}`}>
              {count}
            </span>
          )}
        </div>
        {meta?.description && (
          <div className={`text-[11px] font-normal mt-0.5 leading-tight ${active ? "text-white/70" : "text-gray-500"}`}>
            {meta.description}
          </div>
        )}
        {meta?.useWhen && (
          <div className={`text-[10px] italic mt-0.5 leading-tight ${active ? "text-amber-300" : "text-amber-700"}`}>
            💡 {meta.useWhen}
          </div>
        )}
      </div>
    </button>
  );
}
